import React, { useEffect, useState } from "react";
import {useNavigate, useParams} from "react-router-dom";
import { Button, Space, Dropdown, Menu, Empty, Modal } from "antd";
import {
  DiffOutlined, PlusOutlined
} from "@ant-design/icons";
import Datatable from "../../../shared/components/Datatable";
import { useTranslation } from "react-i18next";
import { ColumnsType } from "antd/lib/table";
import ApplicationService from "../../../../services/application/ApplicationService";
import PageHead from "../../../layout/PageHead";
import { ROUTES } from "../../../../config/consts/routes";
import {NOTIFICATION_TYPES, SECTIONS} from "../../../../config/constants";
import "./CustomQueries.css";
import NotificationsUtil from "../../../shared/utils/NotificationsUtil";
import GroupCodesService from "../../../../services/groupcodes/GroupCodesService";
import TablesService from "../../../../services/tables/TablesService";
import {useSelector} from "react-redux";

const CustomQueries = () => {
  const {
    groupCode,
    entity,
    operation
  } = useSelector((state: any) => ({
    groupCode: state.groupCode,
    entity: state.entity,
    operation: state.operation,
  }));
  
  const { t, i18n } = useTranslation("translations");
  const [queries, setQueries] = useState<any[]>([]);
  const [filteredQueries, setFilteredQueries] = useState<any[]>([]);
  const [groupCodes, setGroupCodes] = useState<any>([]);
  const [entities, setEntities] = useState<any>([]);
  const [operations, setOperations] = useState<any>([]);
  
  const [selectedGroupCode, setSelectedGroupCode] = useState<any>(groupCode);
  const [selectedEntity, setSelectedEntity] = useState<any>(entity);
  const [selectedOperation, setSelectedOperation] = useState<any>(operation);
  
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
  
  useEffect(() => {
    setIsTableLoading(true);
    ApplicationService.getCustomQueries().then((response: any) => {
      setQueries(response.data);
      
      let tableQueries = response.data;
      if (selectedGroupCode) {
        tableQueries = tableQueries.filter((x:any) => x.groupCode == selectedGroupCode);
      }
      if (selectedEntity) {
        tableQueries = tableQueries.filter((x:any) => x.entityId == selectedEntity);
      }
      setFilteredQueries(tableQueries);
    }).finally(() => {
      setIsTableLoading(false);
    });
  }, []);

  useEffect(() => {
    setIsTableLoading(true);
    let tableQueries = queries;
    if (selectedGroupCode) {
      tableQueries = tableQueries.filter((x:any) => x.groupCode == selectedGroupCode);
    }
    if (selectedEntity) {
      tableQueries = tableQueries.filter((x:any) => x.entityId == selectedEntity);
    }
    setFilteredQueries(tableQueries);
    setIsTableLoading(false);
  }, [selectedGroupCode, selectedEntity]);

  useEffect(() => {
    GroupCodesService.getAll().then((response: any) => {
      const data = response.data;
      setGroupCodes(data);
    });
    
    
    TablesService.getAllEntities().then((response: any) => {
      const data = response.data;
      setEntities(data);
    });
    
    TablesService.getAllOperations().then((response: any) => {
      const data = response.data;
      setOperations(data);
    });
  }, []);
  

  const navigate = useNavigate();

  const handleEditClick = (record: any) => {
    navigate(`/report/custom-queries/${record.id}`);
  };

  const handleDeleteClick = (id: number) => {
    ApplicationService.deleteCustomQuery(id.toString())
        .then((response: any) => {
          setIsTableLoading(true);
          setFilteredQueries((prevQueries) => prevQueries.filter((query: any) => query.id !== id));
          setQueries((prevQueries) => prevQueries.filter((query: any) => query.id !== id));
          setIsTableLoading(false);
          NotificationsUtil.openNotificationDialog({
            description: t(
                "Page.Operation.CustomQueries.Notifications.CustomQuery.DeleteSuccessDescription",
                {
                  lngs: i18n.languages,
                  defaultValue: "Custom Query deleted successfully.",
                }
            )!,
            type: NOTIFICATION_TYPES.SUCCESS,
            logError: false,
          });
        })
        .catch((error: any) => {
          NotificationsUtil.openNotificationDialog({
            description: error.response.data,
            type: NOTIFICATION_TYPES.ERROR,
            logError: true,
          });
        });
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      render: (value:any, record:any, index:any) => (
          <span className="label-column">{value}</span>
      ),
      width: 350,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (value:any, record:any, index:any) => (
          <span className="overview-column">{value}</span>
      ),
    },
      
    {
      title: 'Group Code',
      dataIndex: 'groupCode',
      key: 'groupCode',
      ellipsis: true,
      render: (value:any, record:any, index:any) => (
          <span className="overview-column">{value}</span>
      ),
    },
    {
      title: 'Entity',
      dataIndex: 'entity',
      key: 'entity',
      ellipsis: true,
      render: (value:any, record:any, index:any) => (
          <span className="overview-column">{value}</span>
      ),
    },
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
      render: (value:any, record:any, index:any) => (
          <span className="overview-column">{value}</span>
      ),
    },
    {
      title: 'Result',
      dataIndex: 'result',
      key: 'result',
      width: 90,
      render: (value:any, record:any, index:any) => (
          <span className="overview-column">{value}</span>
      ),
    },
    {
      title: 'Last Updated',
      dataIndex: 'sysTimeUpdated',
      key: 'sysTimeUpdated',
      render: (value:any, record:any, index:any) => (
          <span className="overview-column">{value}</span>
      ),
    },
    {
      title: 'Enabled',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (text, record) => (record.isActive ? <span className="overview-column" style={{color: 'green'}}>Yes</span> :<span style={{color: 'red'}}>No</span>),
      width: 80,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (value:any, record:any, index:any) => (
          <span className="overview-column">{value}</span>
      ),
    },
  ];

  const routes = [
    {
      path: ROUTES.general.main.route,
      breadcrumbName: SECTIONS.general.name,
    },
    {
      breadcrumbName: SECTIONS.report.name,
    },
    {
      path: ROUTES.report.customQueries.route,
      breadcrumbName: ROUTES.report.customQueries.name,
    },
  ];

  useEffect(() => {
    setIsTableLoading(true);
    ApplicationService.getCustomQueries()
        .then((response: any) => {
          setQueries(response.data);
        })
        .finally(() => {
          setIsTableLoading(false);
        });
  }, []);

  const newQueryButton = (
      <Button
          type={"primary"}
          onClick={() => handleEditClick(-1)}
          style={{ borderColor: "var(--white)" }}
      >
        <PlusOutlined />
        NEW QUERY
      </Button>
  );

  const scroll = { y: "calc(100vh - 320px)" };

  return (
      <div>
        <PageHead
            title={t("Page.Report.CustomQueries.Headers.CustomQueries", {
              lngs: i18n.languages,
              defaultValue: "Overview",
            })}
            icon={<DiffOutlined />}
            routes={routes}
            groupCodes={groupCodes}
            entities={entities}
            operations={operations}
            selectedGroupCode={selectedGroupCode}
            selectedEntity={selectedEntity}
            selectedOperation={selectedOperation}
            setSelectedGroupCode={setSelectedGroupCode}
            setSelectedEntity={setSelectedEntity}
            setSelectedOperation={setSelectedOperation}
            setSelectGroupCodeDisabled={false}
            setSelectEntityDisabled={false}
            setSelectOperationDisabled={false}
            mainAction={newQueryButton}
            otherActions={[]}
            setActionsDisabled={false}
        />
        <div className="body">
          {filteredQueries.length === 0 ? (
              <Empty description={t("No queries found")} />
          ) : (
              <div className="query-table">
                <Datatable
                    scroll={scroll}
                    tableColumns={columns}
                    data={filteredQueries}
                    setData={setQueries}
                    isTableLoading={isTableLoading}
                    otherActions={[]}
                    otherRowEnabled={false}
                    editRowEnabled={true}
                    copyRowEnabled={false}
                    deleteRowEnabled={true}
                    onEditFunc={handleEditClick}
                    onCopyFunc={undefined}
                    onDeleteFunc={handleDeleteClick}
                    onDoubleClickRow={handleEditClick}
                    selectable={false}
                    multiSelect={false}
                    selectedRows={undefined}
                    setSelectedRows={undefined}
                    paginationVisible={true}
                    showActionsColumn={true}
                />
              </div>

          )}
        </div>
 
    
      </div>
  );
};

export default CustomQueries;